import { useEffect, useMemo, useState } from "react";
import { Users, Truck, Scale } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  AuthUserRelationChange,
  OrganizationMember,
  useUpdateAuthUserRelations,
} from "@/lib/api/betterAuth";
import { CustomerMultiPicker } from "./CustomerMultiPicker";

interface CustomerPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  member: OrganizationMember | null;
}

type RelationType = "risk" | "freight" | "weight_ticket";

interface SectionConfig {
  key: RelationType;
  title: string;
  icon: React.ReactNode;
  maxAllowed?: number;
}

const SECTIONS: SectionConfig[] = [
  {
    key: "risk",
    title: "Risk Profiles",
    icon: <Users className="h-4 w-4" />,
  },
  {
    key: "freight",
    title: "Freight Customers",
    icon: <Truck className="h-4 w-4" />,
    maxAllowed: 1,
  },
  {
    key: "weight_ticket",
    title: "Weight Ticket Customers",
    icon: <Scale className="h-4 w-4" />,
  },
];

export function CustomerPermissionsModal({
  open,
  onClose,
  member,
}: CustomerPermissionsModalProps) {
  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Customer Permissions</DialogTitle>
          <p className="text-sm text-gray-600">
            {member.authuser?.name || "Unknown User"}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {SECTIONS.map((section: SectionConfig) => (
            <CustomerPermissionsSection
              key={section.key}
              member={member}
              section={section}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Thin wrapper around CustomerMultiPicker that owns local selection / search /
 * open state for a single relation type on an existing member, initializes from
 * the member's persisted relations, and saves changes via the relations API.
 *
 * Starts collapsed; header label reflects the live unsaved selection (delegated
 * to CustomerMultiPicker).
 */
interface SectionProps {
  member: OrganizationMember;
  section: SectionConfig;
}

function CustomerPermissionsSection({ member, section }: SectionProps) {
  const { toast } = useToast();
  const updateRelationsMutation = useUpdateAuthUserRelations();

  const initialSelectedIds = useMemo(() => {
    return (member.relations || [])
      .filter((rel) => rel.relation_type === section.key)
      .map((rel) => rel.customer_id);
  }, [member.relations, section.key]);

  const [selectedCustomerIds, setSelectedCustomerIds] =
    useState<string[]>(initialSelectedIds);
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Re-sync local state if the underlying member relations change (e.g. after
  // a successful save invalidates and refetches the member).
  useEffect(() => {
    setSelectedCustomerIds(initialSelectedIds);
  }, [initialSelectedIds]);

  const persistedIdSet = useMemo(() => new Set(initialSelectedIds), [initialSelectedIds]);
  const selectedIdSet = useMemo(() => new Set(selectedCustomerIds), [selectedCustomerIds]);

  const hasChanges = useMemo(() => {
    if (persistedIdSet.size !== selectedIdSet.size) return true;
    for (const id of selectedIdSet) {
      if (!persistedIdSet.has(id)) return true;
    }
    return false;
  }, [persistedIdSet, selectedIdSet]);

  const isMaxExceeded =
    section.maxAllowed !== undefined && selectedCustomerIds.length > section.maxAllowed;

  const handleReset = () => {
    setSelectedCustomerIds(initialSelectedIds);
  };

  const handleSave = async () => {
    if (!member.authuser?.id) {
      toast({
        title: "Error",
        description: "User ID is required",
        variant: "destructive",
      });
      return;
    }

    const changes: AuthUserRelationChange[] = [];

    // The relations API currently types `relation_type` as `"risk"` only, but
    // the server accepts all three. Cast through `as "risk"` to match the
    // existing pattern in CustomerAssignmentEditor.
    const relationType = section.key as "risk";
    for (const customerId of selectedCustomerIds) {
      if (!persistedIdSet.has(customerId)) {
        changes.push({
          user_id: member.authuser.id,
          customer_id: customerId,
          relation_type: relationType,
        });
      }
    }
    for (const persistedId of initialSelectedIds) {
      if (!selectedIdSet.has(persistedId)) {
        changes.push({
          user_id: member.authuser.id,
          customer_id: persistedId,
          relation_type: relationType,
          should_delete: true,
        });
      }
    }

    if (changes.length === 0) {
      toast({ title: "No changes", description: "No changes to save" });
      return;
    }

    try {
      const result = await updateRelationsMutation.mutateAsync({ changes });
      if (!result.success) {
        throw result;
      }

      const additionsCount = changes.filter((c: AuthUserRelationChange) => !c.should_delete).length;
      const deletionsCount = changes.filter((c: AuthUserRelationChange) => c.should_delete === true).length;

      let description = "";
      if (additionsCount > 0 && deletionsCount > 0) {
        description = `Successfully updated: ${additionsCount} added, ${deletionsCount} removed`;
      } else if (additionsCount > 0) {
        description = `Successfully assigned ${additionsCount} customer${additionsCount !== 1 ? "s" : ""}`;
      } else if (deletionsCount > 0) {
        description = `Successfully removed ${deletionsCount} customer${deletionsCount !== 1 ? "s" : ""}`;
      }

      toast({ title: "Success", description });
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message: unknown }).message)
          : "Failed to update customer assignments";
      toast({ title: "Error", description: message, variant: "destructive" });
    }
  };

  const isSaveDisabled =
    updateRelationsMutation.isLoading || isMaxExceeded || !hasChanges;

  return (
    <CustomerMultiPicker
      relationType={section.key}
      title={section.title}
      icon={section.icon}
      selectedCustomerIds={selectedCustomerIds}
      onChange={setSelectedCustomerIds}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      maxAllowed={section.maxAllowed}
      disabled={updateRelationsMutation.isLoading}
      footer={
        hasChanges && (
          <div className="flex justify-between pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={updateRelationsMutation.isLoading}
            >
              Reset Changes
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaveDisabled}
              title={
                isMaxExceeded
                  ? `Maximum of ${section.maxAllowed} customer${section.maxAllowed !== 1 ? "s" : ""} allowed`
                  : undefined
              }
            >
              {updateRelationsMutation.isLoading ? "Saving..." : "Save Assignments"}
            </Button>
          </div>
        )
      }
    />
  );
}
